import { FC, useEffect, useMemo, useState } from "react";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useParams } from "react-router-dom";
import { Formik } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import {
  useActiveAppStockMutation,
  useAddStockMutation,
  useCategoriesQuery,
  useCreateProductMutation,
  useDeleteProductPermanentlyMutation,
  useDownloadStockHistoryMutation,
  useDownloadStockMutation,
  useProductsStoreQuery,
  useSubtractStockMutation,
  useUpdateProductMutation,
} from "@/app/products/products.api";
import { InputDropDownSpan } from "@/shared/components/atoms/InputDropDownSpan";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";

import EllipsesMenuStock from "@/shared/components/molecules/EllipsesMenuStock";

import { ModalStore } from "@/shared/components/organisms/ModalStore";
import { Select } from "@/shared/components/organisms/Select";
import PaginationUI from "@/shared/components/atoms/PaginationUI";

import { currencies } from "@/app/fees/fees";
import TableListStockStore from "@/shared/components/organisms/TableListStockStore";
import { useDebounce, useFunctionDebounce } from "@/shared/hooks/debounce";
import {
  productInitialValues,
  productValidationSchema,
  ProductValues,
} from "@/shared/schemas/store.schema";

import { CatchmentRequest } from "@/app/catchment/catchment.types";
import { toasts } from "@/shared/utils/toasts";
import { branchApi } from "@/app/branchs/branchs.api";
import { useSelector } from "react-redux";
import { camelToSnake } from "caseparser";
import { InputFile } from "@/shared/components/atoms/InputFile";
import { Avatar } from "@/shared/components/atoms/Avatar";
import { RootState } from "@/app/app.store";
import { FIELD_REQUIRED_MESSAGE } from "@/shared/utils/validations";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { useCurrenciesQuery } from "@/app/accounting/accounting.api";

export const initialValues: CatchmentRequest = {
  search: "",
  branch_filter: null,
  sex_filter: null,
  campaign_filter: null,
  stages_filter: null,
  stages_users_filter: null,
  tag_marketing_filter: null,
  catchment_page: "campaigns",
};

const optionsDivisa = [
  { id: 1, text: "USD" },
  { id: 2, text: "EUR" },
  { id: 3, text: "ARS" },
  { id: 4, text: "COP" },
];

const optionsCategory = [
  { id: 1, name: "Category 1" },
  { id: 2, name: "Category 2" },
  { id: 3, name: "Category 3" },
  { id: 4, name: "Category 4" },
];

export const StockStore: FC<any> = () => {
  const { id } = useParams();
  const [currentProductEdit, setCurrentProductEdit] = useState();
  const [currentProductAdd, setCurrentProductAdd] = useState();
  const [currentProductRemove, setCurrentProductRemove] = useState();
  const [currentProductDelete, setCurrentProductDelete] = useState();
  const [productSearch, setProductSearch] = useState("");
  const debouncedProductSearch = useDebounce(productSearch, 500);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  const handleDebounceCreateProduct = useFunctionDebounce(
    handleCreateProduct,
    200
  );
  const handleDebounceUpdateProduct = useFunctionDebounce(
    handleUpdateProduct,
    200
  );

  const categoriesQuery = useCategoriesQuery({ branchId: id });
  const productsQuery = useProductsStoreQuery({
    branchId: id,
    search: debouncedProductSearch,
    page: debouncedProductSearch ? 1 : page,
  });
  const userId = useSelector((state: RootState) => state?.auth?.user?.id);

  const isAppStockActive = useSelector(
    (state: any) => state.branch.branch.in_app_store
  );

  const [createProduct, { isLoading: isLoadingCreateProduct }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: isLoadingUpdateProduct }] =
    useUpdateProductMutation();
  const [downloadStock] = useDownloadStockMutation();
  const [downloadStockHistory] = useDownloadStockHistoryMutation();
  const [activeAppStock] = useActiveAppStockMutation();
  const [addStock, addStockResult] = useAddStockMutation();
  const [subtractStock, subtractStockResult] = useSubtractStockMutation();
  const [deleteProductPermanently, deleteProductPermanentlyResult] =
    useDeleteProductPermanentlyMutation();
  const debouncedHandleUpdateStock = useFunctionDebounce(
    handleUpdateStock,
    200
  );
  const debouncedHandleRemoveStock = useFunctionDebounce(
    handleRemoveStock,
    200
  );
  const tabs = [
    {
      id: "campaigns",
      name: t("store.views.tabs.sell_products"),
    },
    {
      id: "stock",
      name: t("store.views.tabs.manage_stock"),
    },
  ];

  const [openAddStock, setOpenAddStock] = useState(false);
  const handleOpenAddStock = () => {
    setOpenAddStock(!openAddStock);
  };
  const [openDeleteStock, setOpenDeleteStock] = useState(false);
  const handleOpenDeleteStock = () => {
    setOpenDeleteStock(!openDeleteStock);
  };
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const handleOpenDialogCreate = () => {
    setOpenDialogCreate(!openDialogCreate);
  };
  const [selectCategory, setSelectCategory] = useState(optionsCategory[0]);
  const [openDeleteProductModal, setOpenDeleteProductModal] = useState(false);
  const handleOpenDeleteProductModal = () => {
    setOpenDeleteProductModal(!openDeleteProductModal);
  };
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleDownloadStock = async () => {
    try {
      await downloadStock({ branchId: id }).unwrap(); // 👈 Espera que termine
      toasts.success(
        "Descarga de stock",
        "El stock se ha descargado exitosamente"
      );
    } catch (err) {
      console.error("Error al descargar el stock:", err);
      toasts.danger(
        "Descarga de stock",
        "El stock no se ha descargado exitosamente"
      );
    }
  };

  const handleDownloadStockHistory = async () => {
    try {
      await downloadStockHistory({ branchId: id }).unwrap();
    } catch (err) {
      console.error("Error al descargar el historial:", err);
      toasts.danger(
        "Descarga de historial",
        "El historial no se ha descargado exitosamente"
      );
    }
  };

  async function handleCreateProduct(values: ProductValues) {
    try {
      await createProduct({
        name: values.name,
        description: values.description,
        amount: values.amount,
        price: values.price,
        currency: values.currency,
        reference: values.reference,
        userId,
        branchId: id,
        categoryId: values.categoryId || null,
        category: values.category || null,
        codePromotional: values.codePromotional,
        img: values.img,
        link: values.link,
        cost: values.cost,
        supplier: values.supplier,
      }).unwrap();
      toasts.success(
        "Creación de producto",
        "El producto se ha creado exitosamente"
      );
      handleOpenDialogCreate();
      setCurrentProductEdit(undefined);
      productsQuery.refetch();
    } catch (error) {
      toasts.danger("Creación de producto", "El producto no se ha creado");
      throw error;
    }
  }

  async function handleUpdateProduct(values: ProductValues) {
    try {
      await updateProduct({
        productId: currentProductEdit!.id,
        name: values.name,
        description: values.description,
        amount: values.amount,
        price: values.price,
        currency: values.currency,
        reference: values.reference,
        userId,
        branchId: id,
        category: values.category,
        categoryId: values.categoryId,
        codePromotional: values.codePromotional,
        img: values.img,
        link: values.link,
        cost: values.cost,
        supplier: values.supplier,
      }).unwrap();
      toasts.success(
        "Actualización de producto",
        "El producto se ha actualizado exitosamente"
      );
      setCurrentProductEdit(undefined);
      handleOpenDialogCreate();
      productsQuery.refetch();
    } catch (error) {
      toasts.danger(
        "Actualización de producto",
        "El producto no se ha actualizado"
      );
    }
  }

  async function handleActiveAppStock() {
    try {
      await activeAppStock({ branchId: id }).unwrap();
      toasts.success(
        "Activar App",
        "La aplicación se ha activado exitosamente"
      );
    } catch (err) {
      toasts.danger("Activar App", "La aplicación no se ha activado");
      console.error("Error al activar la aplicación:", err);
    }
  }

  const handleDisableApp = async () => {
    try {
      await activeAppStock({ branchId: id }).unwrap();

      toasts.success(
        "Desactivar App",
        "La aplicación se ha desactivado exitosamente"
      );
    } catch (err) {
      toasts.danger("Desactivar App", "La aplicación no se ha desactivado");
      console.error("Error al desactivar la aplicación:", err);
    }
  };

  const handleManagementEnableApp = async () => {
    try {
      if (isAppStockActive === 1) {
        handleActiveAppStock();
      } else {
        handleDisableApp();
      }
    } catch (err) {
      console.error("Error on handleManagementEnableApp:", err);
    }
  };

  async function handleUpdateStock({ amount }: { amount: number }) {
    try {
      await addStock({
        branchId: id,
        productId: currentProductAdd?.id,
        amount,
      }).unwrap();
      toasts.success(
        "Actualización de stock",
        "El stock se ha actualizado exitosamente"
      );
      setCurrentProductAdd(undefined);
    } catch (error) {
      toasts.danger("Actualización de stock", "El stock no se ha actualizado");
      console.log("error", error);
    }
  }

  async function handleRemoveStock({ amount }: { amount: number }) {
    try {
      await subtractStock({
        branchId: id,
        productId: currentProductRemove?.id,
        amount,
      }).unwrap();
      toasts.success(
        "Actualización de stock",
        "El stock se ha restado exitosamente"
      );
      setCurrentProductRemove(undefined);
    } catch (error) {
      toasts.danger("Actualización de stock", "El stock no se ha actualizado");
      console.log("error", error);
    }
  }

  const handleDeleteProduct = async () => {
    try {
      if (currentProductDelete?.amount > 0) {
        toasts.danger(
          "Eliminación de producto",
          "Elimina el stock de este producto antes de borrarlo definitivamente"
        );
        return;
      }

      await deleteProductPermanently({
        branchId: id,
        productId: currentProductDelete?.id,
      }).unwrap();
      toasts.success(
        "Eliminación de producto",
        "El producto se ha eliminado definitivamente"
      );
      handleOpenDeleteProductModal();
      setCurrentProductDelete(undefined);
      productsQuery.refetch();
    } catch (error) {
      toasts.danger(
        "Eliminación de producto",
        "El producto no se ha eliminado"
      );
    }
  };

  const productList = useMemo(() => {
    return productsQuery?.data?.data ?? [];
  }, [productsQuery.data]);

  return (
    <div className="flex flex-col flex-1 w-full">
      <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
        <div className="flex flex-col md:flex-row md:gap-x-2 px-4 bottom pb-2 border-b mt-2">
          <div className="flex flex-col flex-1 gap-4 md:gap-2 md:flex-row md:items-start">
            <div className="col-span-full">
              <InputWithCornerHint
                label={t("store.views.form.search")}
                type="text"
                placeholder={t("store.views.form.search_by_name")}
                value={productSearch}
                onChange={(event) => setProductSearch(event.target.value)}
              />
            </div>
          </div>
          <div className="flex md:flex-row md:items-center gap-3 gap-x-2 md:gap-x-2 pt-6 md:pt-0 md:self-end">
            <button
              onClick={handleOpenDialogCreate}
              type="button"
              className="mb-1 block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
            >
              <PlusIcon aria-hidden="true" className="h-5 w-5" />
            </button>
            <EllipsesMenuStock
              OnDownloadHistory={handleDownloadStockHistory}
              OnDownloadStock={handleDownloadStock}
              OnActiveDeactivatedApp={handleManagementEnableApp}
              isAppStockActive={isAppStockActive === 0}
            />
          </div>
        </div>

        <div className="flex-1">
          <TableListStockStore
            isLoading={productsQuery.isLoading}
            onAddStock={setCurrentProductAdd}
            onDeleteStock={setCurrentProductRemove}
            onDeleteProduct={(product) => {
              handleOpenDeleteProductModal();
              setCurrentProductDelete(product);
            }}
            onEdit={(product) => {
              setCurrentProductEdit(product);
              handleOpenDialogCreate();
            }}
            products={productList ?? []}
            onCreate={handleOpenDialogCreate}
          />
          {productList && (
            <PaginationUI
              currentPage={debouncedProductSearch ? 1 : page}
              totalPages={Math.ceil(
                (productsQuery?.data?.total ?? 0) / pageSize
              )}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              totalResults={productsQuery?.data?.total ?? 0}
            />
          )}
        </div>

        {/* Delete product */}
        <AlertErrorButtons
          open={openDeleteProductModal}
          onClose={() => handleOpenDeleteProductModal()}
          title={t("store.views.delete_confirmation_title")}
          message={t("store.views.delete_confirmation_message")}
          isLoading={deleteProductPermanentlyResult.isLoading}
          primaryButtonText={t("general.delete")}
          secondaryButtonText={t("general.cancel")}
          onPrimaryButtonClick={handleDeleteProduct}
          onSecondaryButtonClick={() => handleOpenDeleteProductModal()}
        />

        <Formik
          initialValues={{ amount: undefined }}
          validationSchema={Yup.object({
            amount: Yup.number()
              .required(FIELD_REQUIRED_MESSAGE)
              .min(1, "El stock debe ser mayor a 0"),
          })}
          onSubmit={async (values, formikHelpers) => {
            try {
              await debouncedHandleUpdateStock(values);
              formikHelpers.resetForm();
            } catch (error) {
              console.error("Error al actualizar el stock:", error);
            }
          }}
        >
          {({
            handleChange,
            handleSubmit,
            handleBlur,
            values,
            errors,
            touched,
          }) => {
            return (
              <ModalStore
                onClick={handleSubmit}
                onClose={() => setCurrentProductAdd(undefined)}
                open={!!currentProductAdd}
                acceptButtonText={t("store.views.form.add")}
                cancelButtonText={t("store.views.form.cancel")}
                title={t("store.views.add_stock")}
                isLoading={addStockResult.isLoading}
              >
                <div className="text-center mt-5">
                  <label
                    htmlFor=""
                    className="block text-sm font-medium leading-6 text-gray-600"
                  >
                    {t("store.views.add_stock_subtitle")}
                  </label>
                </div>
                <div className="col-span-full px-4 md:px-8 lg:px-16 text-center">
                  <InputWithCornerHint
                    label=""
                    className="w-full"
                    type="number"
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched?.amount && errors?.amount}
                    textError={errors?.amount}
                    min="1"
                    max="999"
                  />
                </div>
              </ModalStore>
            );
          }}
        </Formik>

        <Formik
          initialValues={{ amount: undefined }}
          validationSchema={Yup.object({
            amount: Yup.number()
              .required(FIELD_REQUIRED_MESSAGE)
              .min(1, "El stock debe ser mayor a 0"),
          })}
          onSubmit={async (values, formikHelpers) => {
            try {
              await debouncedHandleRemoveStock(values);
              formikHelpers.resetForm();
            } catch (error) {
              console.error("Error al restar el stock:", error);
            }
          }}
        >
          {({ handleChange, handleSubmit, handleBlur, values }) => {
            return (
              <ModalStore
                onClick={handleSubmit}
                onClose={() => setCurrentProductRemove(undefined)}
                open={!!currentProductRemove}
                acceptButtonText={t("general.remove")}
                cancelButtonText={t("store.views.form.cancel")}
                title={t("store.views.sub_stock")}
                isLoading={subtractStockResult.isLoading}
              >
                <div className="text-center mt-5">
                  <label
                    htmlFor=""
                    className="block text-sm font-medium leading-6 text-gray-600"
                  >
                    {t("store.views.sub_stock_subtitle")}
                  </label>
                </div>
                <div className="col-span-full px-32 text-center">
                  <InputWithCornerHint
                    label=""
                    type="number"
                    name="amount"
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </div>
              </ModalStore>
            );
          }}
        </Formik>

        {/* Dialogo de creación de producto */}
        <Formik
          initialValues={{
            ...(currentProductEdit ?? productInitialValues),
            currency: currentProductEdit?.currency || "EUR",
            category: "",
          }}
          validationSchema={productValidationSchema}
          enableReinitialize
          onSubmit={async (values: ProductValues, formikHelpers) => {
            if (currentProductEdit) {
              try {
                await handleDebounceUpdateProduct(values);
                formikHelpers.resetForm();
              } catch (error) {
                console.error("Error al actualizar el producto:", error);
              }
            } else {
              try {
                await handleDebounceCreateProduct(values);
                formikHelpers.resetForm();
              } catch (error) {
                console.error("Error al crear el producto:", error);
              }
            }
          }}
        >
          {({
            handleChange,
            handleSubmit,
            handleBlur,
            setFieldValue,
            resetForm,
            values,
            errors,
            touched,
          }) => (
            <DialogCreate
              open={openDialogCreate}
              setOpen={handleOpenDialogCreate}
              tittle={
                currentProductEdit
                  ? t("store.views.edit_product")
                  : t("store.views.add_new_product")
              }
              tittleButtonCreate={
                currentProductEdit ? t("general.edit") : t("general.save")
              }
              tittleButtonCancel={t("general.cancel")}
              isLoading={isLoadingCreateProduct || isLoadingUpdateProduct}
              acceptDisabled={isLoadingCreateProduct || isLoadingUpdateProduct}
              OnclickCreate={handleSubmit}
              onClose={() => {
                handleOpenDialogCreate();
                resetForm();
                setCurrentProductEdit(undefined);
              }}
              OnclickCancel={() => {
                handleOpenDialogCreate();
                resetForm();
                setCurrentProductEdit(undefined);
              }}
            >
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="col-span-full">
                      <p className="text-sm text-gray-400 md:p-5">
                        {t("store.views.external_link_warning")}{" "}
                      </p>
                    </div>
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("store.views.caracteristics_product")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputFile
                          name="img"
                          id="img"
                          label={t("user.views.avatar")}
                          spanButton={t("user.views.avatar")}
                          description={t("user.views.avatar")}
                          selectedImage={values?.img}
                          onFileChange={(file) => {
                            const reader = new FileReader();
                            reader.onload = (e) => {
                              setFieldValue("img", e.target?.result);
                            };
                            reader.readAsDataURL(file);
                          }}
                          tittleDelete={t("general.delete_image")}
                          onRemoveImage={() => {
                            setFieldValue("img", null);
                          }}
                        />

                        {errors?.img && touched?.img && (
                          <p className="text-red-500 text-sm mt-2">
                            {errors.img as string}
                          </p>
                        )}
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.name")}
                          span={t("general.mandatory")}
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.name && !!errors.name}
                          textError={touched.name && errors.name}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.description")}
                          span={t("general.mandatory")}
                          type="text"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.description && !!errors.description}
                          textError={touched.description && errors.description}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.reference")}
                          span={t("general.optional")}
                          type="text"
                          name="reference"
                          value={values.reference}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.reference && !!errors.reference}
                          textError={touched.reference && errors.reference}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.amount")}
                          span={t("general.optional")}
                          type="number"
                          name="amount"
                          value={values.amount}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.amount && !!errors.amount}
                          textError={touched.amount && errors.amount}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputDropDownSpan
                          label={t("customer-to-gym-subscription.views.mount")}
                          span={t("general.mandatory")}
                          name="price"
                          type="number"
                          placeholder={t(
                            "customer-to-gym-subscription.views.mount"
                          )}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value);
                            handleChange({
                              target: {
                                name: e.target.name,
                                value: isNaN(value) ? "" : value,
                              },
                            });
                          }}
                          value={values.price}
                          nameSelect="currency"
                          selectValue={values.currency.toUpperCase()}
                          onSelectChange={handleChange}
                          options={currencies.map((item) => ({
                            ...item,
                            id: item.currency,
                            text: item.currency,
                          }))}
                          error={
                            (touched.price && !!errors.price) ||
                            (touched.currency && !!errors.currency)
                          }
                          textError={
                            (touched.price && errors.price) ||
                            (touched.currency && errors.currency)
                          }
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.cost")}
                          span={t("general.mandatory")}
                          type="number"
                          name="cost"
                          value={values.cost}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={errors?.cost && touched?.cost}
                          textError={errors?.cost}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("store.views.info_prom")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("accounting.views.supplier")}
                          span={t("general.optional")}
                          type="text"
                          name="supplier"
                          value={values.supplier}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.supplier && !!errors.supplier}
                          textError={touched.supplier && errors.supplier}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.link")}
                          span={t("general.optional")}
                          type="text"
                          name="link"
                          value={values.link}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.link && !!errors.link}
                          textError={touched.link && errors.link}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.promotional_code")}
                          span={t("general.optional")}
                          type="text"
                          name="codePromotional"
                          id="codePromotional"
                          value={values.codePromotional}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            touched.codePromotional && !!errors.codePromotional
                          }
                          textError={
                            touched.codePromotional && errors.codePromotional
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("store.views.category")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <Select
                          tittle={t(
                            "store.views.form.select_category_from_list"
                          )}
                          span={t("general.mandatory")}
                          selected={categoriesQuery.data?.find(
                            (category) => category.id == values.categoryId
                          )}
                          setSelected={(option) => {
                            setFieldValue("categoryId", option.id);
                          }}
                          options={categoriesQuery.data}
                          error={errors?.categoryId && touched?.categoryId}
                          textError={
                            errors?.categoryId && touched?.categoryId
                              ? errors?.categoryId
                              : ""
                          }
                        />
                      </div>

                      {/* Div con la "O" centrada */}
                      <div className="text-center text-gray-900 font-semibold">
                        {t("slots.views.o")}
                      </div>

                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("store.views.form.type_new_category_name")}
                          type="text"
                          name="category"
                          id="category"
                          value={values.category}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.category && errors?.category}
                          textError={errors?.category}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </DialogCreate>
          )}
        </Formik>
      </div>
    </div>
  );
};
